import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Inbox, Plus, FileText, ArrowRight, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/estimatesData';
import { statusLabel } from '@/lib/statusLabels';
import {
  normalizeStatus,
  isArchiveStatus,
  reasonsForStatus,
} from '@/lib/estimateLifecycle';

// Phase 27 — lifecycle badge map. Keys mirror the canonical statuses from
// @/lib/estimateLifecycle. Legacy 'approved' / 'rejected' values are
// normalized at hydrate-time so they never reach this map at runtime.
const statusBadge = {
  draft: 'bg-[hsl(var(--muted))] text-foreground border-border',
  sent: 'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30',
  won:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  lost:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
};

// Translate a stored outcome_reason value into its localized label.
// Stored values match the LOST_REASONS/WON_REASONS value field; if a custom
// free-text override is present we display it as-is.
const formatOutcomeReason = (t, status, raw) => {
  if (!raw) return '';
  const opts = reasonsForStatus(status);
  const match = opts.find((o) => o.value === raw);
  if (match) return t(match.i18nKey, { defaultValue: raw });
  return raw;
};

export const EstimatesListSkeleton = () => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
    <CardContent className="p-4 space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-3">
          <Skeleton className="h-3 w-1/3 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-3 w-1/4 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-3 w-20 bg-[hsl(var(--muted))]" />
        </div>
      ))}
    </CardContent>
  </Card>
);

export const EstimatesList = ({
  estimates = [],
  onOpen,
  onCreate,
  onDelete,
}) => {
  const { t } = useTranslation();
  if (!estimates.length) {
    return (
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="estimates-empty"
      >
        <CardContent className="py-12 px-6 text-center">
          <div className="mx-auto size-10 rounded-md bg-[hsl(var(--muted))] border border-border flex items-center justify-center text-[hsl(var(--primary))]">
            <Inbox size={20} strokeWidth={1.75} />
          </div>
          <h3 className="mt-3 text-sm sm:text-base font-semibold text-foreground">
            {t('estimates.list.empty_title')}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-muted-foreground max-w-sm mx-auto">
            {t('estimates.list.empty_help')}
          </p>
          {onCreate ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onCreate}
              className="mt-4 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="estimates-empty-create"
            >
              <Plus size={14} className="mr-2" />
              {t('estimates.list.new_button')}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('estimates.list.saved')}
            </div>
            <span className="text-xs text-muted-foreground ml-1">
              {t('estimates.list.total_count', { count: estimates.length })}
            </span>
          </div>
        </div>
        {/* Below xl: one card per estimate. The table needs ~700px, so on a
            phone (and a tablet / small laptop beside the sidebar) its Status,
            Total and Open button sat off-screen in a sideways scroll — an
            estimate couldn't be opened without knowing to swipe. */}
        <ul className="xl:hidden space-y-2" data-testid="estimates-cards">
          {estimates.map((est) => {
            const status = normalizeStatus(est.status || 'draft');
            return (
              <li
                key={est.id}
                className="rounded-md border border-border p-3"
                data-testid={`estimate-card-${est.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground break-words">
                      {est.project_name || est.title || t('estimates.list.untitled')}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground break-words">
                      {est.client_name || '—'}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-sm font-semibold text-foreground tabular-nums">
                    {formatUsd(est.total)}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-col gap-1 min-w-0">
                    <Badge
                      variant="outline"
                      className={cn(
                        'uppercase text-[10px] tracking-[0.16em] w-fit',
                        statusBadge[status] || statusBadge.draft
                      )}
                    >
                      {statusLabel(t, status)}
                    </Badge>
                    {isArchiveStatus(est.status) && est.outcome_reason ? (
                      <span className="text-[10px] text-muted-foreground italic break-words">
                        {formatOutcomeReason(t, est.status, est.outcome_reason)}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpen?.(est)}
                      className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px] h-8"
                      data-testid={`estimate-card-open-${est.id}`}
                    >
                      {t('estimates.list.open')}
                      <ArrowRight size={12} className="ml-1" />
                    </Button>
                    {onDelete ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDelete?.(est)}
                        className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-8 w-8"
                        aria-label={t('estimates.list.delete_aria')}
                        data-testid={`estimate-card-delete-${est.id}`}
                      >
                        <Trash2 size={13} />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="hidden xl:block rounded-md border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('estimates.list.project')}
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('estimates.list.client')}
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('estimates.list.status')}
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('estimates.list.total')}
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground w-32">
                  &nbsp;
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {estimates.map((est) => (
                <TableRow
                  key={est.id}
                  className="hover:bg-[hsl(var(--muted))]/40 border-border"
                  data-testid={`estimate-row-${est.id}`}
                >
                  <TableCell className="text-sm text-foreground font-semibold max-w-[320px] truncate">
                    {est.project_name || est.title || t('estimates.list.untitled')}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {est.client_name || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant="outline"
                        className={cn(
                          'uppercase text-[10px] tracking-[0.16em] w-fit',
                          statusBadge[normalizeStatus(est.status)] || statusBadge.draft
                        )}
                        data-testid={`estimate-status-badge-${est.id}`}
                      >
                        {statusLabel(t, normalizeStatus(est.status || 'draft'))}
                      </Badge>
                      {isArchiveStatus(est.status) && est.outcome_reason ? (
                        <span
                          className="text-[10px] text-muted-foreground italic max-w-[220px] truncate"
                          data-testid={`estimate-outcome-reason-${est.id}`}
                          title={formatOutcomeReason(t, est.status, est.outcome_reason)}
                        >
                          {formatOutcomeReason(t, est.status, est.outcome_reason)}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold text-foreground">
                    {formatUsd(est.total)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpen?.(est)}
                        className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px] h-7"
                        data-testid={`estimate-open-${est.id}`}
                      >
                        {t('estimates.list.open')}
                        <ArrowRight size={12} className="ml-1" />
                      </Button>
                      {onDelete ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onDelete?.(est)}
                          className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
                          aria-label={t('estimates.list.delete_aria')}
                          data-testid={`estimate-delete-${est.id}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimatesList;
